import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import EdiText from 'react-editext';
import { RIESelect } from 'riek';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { faBan } from '@fortawesome/pro-solid-svg-icons/faBan';
import { faCircle } from '@fortawesome/pro-solid-svg-icons/faCircle';
import { faPencil } from '@fortawesome/pro-solid-svg-icons/faPencil';
import { faSave } from '@fortawesome/pro-solid-svg-icons/faSave';
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons/faTimesCircle';
import { faUndo } from '@fortawesome/pro-solid-svg-icons/faUndo';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';

import { formattedDate } from 'utils';
import FontAwesome from 'utils/FA';

import 'react-datepicker/dist/react-datepicker.css';

import './Reminder.scss';

const Reminder = ({
  body,
  contact,
  due_date: dueDate,
  id,
  onDestroy,
  onSave,
  showContact,
  user,
  users
}) => {
  const [saving, setSaving] = useState(false);

  // stop people from spamming the complete button
  const complete = () => {
    setSaving(true);
    onSave({ id, completed: true })
      .catch(() => setSaving(false));
  }

  return (<div className='reminder'>
    <OverlayTrigger
      placement='top'
      overlay={<Tooltip id='reminder-delete'>Delete</Tooltip>}
    >
      <button className='reminder-delete reminder-action' type='button' onClick={() => onDestroy({ id })}>
        <FontAwesome icon={faTimesCircle} />
      </button>
    </OverlayTrigger>

    <button disabled={saving} className='reminder-complete' type='button' onClick={() => complete(id)}>
      <FontAwesome icon={faCheck} className='reminder-check' />
    </button>

    <div className='reminder-content'>
      <div className='reminder-header'>
        {!user ? <FontAwesome icon={faBan} className='reminder-user-deleted' postNbsp /> : null}
        <RIESelect
          value={user ? { id: user.id, text: user.name } : { id: '', text: 'User Deleted' }}
          propName='value'
          change={({ value: { id: userId } }) => onSave({ id, owner_id: userId })}
          options={users.map((u) => ({ id: u.id, text: u.name }))}
          className='reminder-user-selector'
        />
      </div>

      <DatePicker
        dateFormat={global.I18n.date.formats.default_date_fns}
        selected={moment(dueDate).toDate()}
        onChange={(v) => onSave({ id, due_date: moment(v).format('YYYY-MM-DD') })}
        showPopperArrow
        popperPlacement='top-end'
        customInput={(
          <button className='reminder-inline reminder-date' type='button'>
            {formattedDate(dueDate)}
          </button>
        )}
      />

      <div className='reminder-body'>
        <EdiText
          type='textarea'
          value={body}
          onSave={(value) => onSave({ id, body: value })}

          editButtonClassName='reminder-edit reminder-action'
          editButtonContent={(
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip id='reminder-edit'>Edit</Tooltip>}
            >
              <span className='fa-layers fa-fw'>
                <FontAwesome icon={faCircle} />
                <FontAwesome icon={faPencil} className='reminder-action-icon' size='xs' />
              </span>
            </OverlayTrigger>
          )}

          cancelButtonClassName='reminder-revert reminder-action'
          cancelButtonContent={(
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip id='reminder-revert'>Revert</Tooltip>}
            >
              <span className='fa-layers fa-fw'>
                <FontAwesome icon={faCircle} />
                <FontAwesome icon={faUndo} className='reminder-action-icon' size='xs' />
              </span>
            </OverlayTrigger>
          )}

          saveButtonClassName='reminder-save reminder-action'
          saveButtonContent={(
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip id='reminder-save'>Save</Tooltip>}
            >
              <span className='fa-layers fa-fw'>
                <FontAwesome icon={faCircle} />
                <FontAwesome icon={faSave} className='reminder-action-icon' size='xs' />
              </span>
            </OverlayTrigger>
          )}

          inputProps={{
            onFocus: (e) => {
              e.target.style.height = '';
              e.target.style.height = `${e.target.scrollHeight - 10}px`;
            },
            onInput: (e) => {
              e.target.style.height = '';
              e.target.style.height = `${e.target.scrollHeight - 10}px`;
            }
          }}
        />
      </div>

      {
        showContact && contact && (
          <div className='reminder-footer'>
            <a href={`/contacts/${contact.id}`}>
              <FontAwesome icon={faUser} />
              &nbsp;
              {contact.display_name}
            </a>
          </div>
        )
      }
    </div>
  </div>)
};

Reminder.defaultProps = {
  body: '',
  contact: {},
  due_date: moment(new Date()).format('YYYY-MM-DD'),
  id: '',
  onSave: () => false,
  onDestroy: () => false,
  showContact: false,
  user: {},
  users: []
};

const userType = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string
});

Reminder.propTypes = {
  body: PropTypes.string,
  contact: PropTypes.shape({
    id: PropTypes.string,
    display_name: PropTypes.string
  }),
  due_date: PropTypes.string,
  id: PropTypes.string,
  onSave: PropTypes.func,
  onDestroy: PropTypes.func,
  showContact: PropTypes.bool,
  user: userType,
  users: PropTypes.arrayOf(userType)
};

export default Reminder;
